import { Autocomplete, createFilterOptions, Popper, TextField } from "@mui/material";
import React, { ChangeEvent, FC, useMemo } from "react";
import VirtualListBox from "../VirtualListBox/VirtualListBox";
import { colorPrimary } from "../../constants/colors";
import { ITextfieldWithAutocomplete, IAutocompleteOption } from "./TextfieldWithAutocomplete.props";
import NoOptionText from "../NoOptionText/NoOptionText";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import RenderOption from "../RenderOption/RenderOption";

const TextfieldWithAutocomplete: FC<ITextfieldWithAutocomplete> = ({
	autocompleteOptions,
	autocompleteOptionsWithId,
	freeSolo = false,
	value,
	label,
	setValues,
	values,
	onChange,
	name,
	inputRef,
	virtualize = false,
	valueFrom = "label",
	shrink,
	...props
}) => {
	// Новый режим (options с id и label). Нормализация списка
	const normalizedOptions = useMemo<IAutocompleteOption[]>(() => {
		if (!autocompleteOptionsWithId) return [];
		return autocompleteOptionsWithId.map((opt) => ({ value: opt.value, label: opt.label }));
	}, [autocompleteOptionsWithId]);

	// Старый режим (уникальные строки). Нормализация списка
	const uniqueOptions = useMemo(() => {
		if (!autocompleteOptions) return [];
		return Array.from(new Set(autocompleteOptions)).sort((a, b) => a.localeCompare(b));
	}, [autocompleteOptions]);

	const handleChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
		onChange(e);
	};

	// Старый режим (options с id и label). Изменение значения
	const handleChangeOld = (_: React.SyntheticEvent, newValue: string | null) => {
		if (setValues) {
			setValues({ ...values, [name]: newValue ?? "" });
		}
	};

	// Новый режим (options с id и label). Изменение значения
	const handleChangeNew = (_: React.SyntheticEvent, newValue: IAutocompleteOption | string | null) => {
		if (!setValues) return;

		let idValue: string | number = "";
		if (newValue === null) {
			idValue = "";
		} else if (typeof newValue === "string") {
			idValue = newValue;
		} else {
			idValue = newValue.value;
		}

		setValues({ ...values, [name]: idValue });
	};

	const filterOptions = createFilterOptions<IAutocompleteOption>({
		stringify: (option) => `${option.label} ${option.value}`, // ищем и по label, и по value
	});

	if (autocompleteOptionsWithId) {
		// Новый режим
		return (
			<Autocomplete<any, false, boolean, boolean>
				{...props}
				freeSolo={freeSolo}
				autoHighlight
				disableListWrap
				openOnFocus
				options={normalizedOptions}
				value={normalizedOptions.find((opt) => opt.value === value) || value || null}
				filterOptions={filterOptions}
				onChange={handleChangeNew}
				// onBlur={() => {
				// 	const currentOption = normalizedOptions.find((opt) => opt.value === value);
				// 	if (!currentOption && value) {
				// 		// сохраняем введённый текст даже если он не из списка
				// 		if (setValues) {
				// 			setValues({ ...values, [name]: value });
				// 		}
				// 	}
				// }}
				getOptionLabel={(option) => (typeof option === "string" ? option : valueFrom === "label" ? option.label : option.value)}
				isOptionEqualToValue={(option, val) => (typeof val === "string" ? option.value === val : option.value === val.value)}
				ListboxComponent={virtualize ? VirtualListBox : undefined}
				noOptionsText={<NoOptionText />}
				disableClearable={!value}
				clearIcon={<FontAwesomeIcon icon={faXmark} size="xs" />}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						name={name}
						inputRef={inputRef}
						// onChange={handleChangeInput}
						InputLabelProps={{ shrink: shrink, style: { fontSize: 13 } }}
						sx={{ ".MuiInputLabel-root[data-shrink='false']": { top: -8 } }}
					/>
				)}
				renderOption={(props, option) => (
					<RenderOption {...props} key={option.id} style={{ padding: "8px 18px", fontSize: 12 }}>
						{option.label}
					</RenderOption>
				)}
				PopperComponent={(props) => <Popper {...props} sx={{ boxShadow: `0 0 5px 1px ${colorPrimary}`, borderRadius: 3 }} />}
				sx={{
					".MuiInputBase-root": { height: 33, fontSize: 13, color: colorPrimary },
					".MuiInputBase-input": { marginTop: -1 },
					".MuiOutlinedInput-root": { padding: "0 0 0 9px", paddingRight: "30px !important" },
				}}
			/>
		);
	} else if (autocompleteOptions && uniqueOptions.length > 0) {
		// Старый режим
		return (
			<Autocomplete<string, false, boolean, boolean>
				{...props}
				freeSolo={freeSolo}
				autoHighlight
				disableListWrap
				openOnFocus
				options={uniqueOptions}
				value={value}
				onChange={handleChangeOld}
				ListboxComponent={virtualize ? VirtualListBox : undefined}
				renderOption={(props, option) => (
					<RenderOption {...props} key={option} style={{ padding: "8px 14px" }}>
						{option}
					</RenderOption>
				)}
				onBlur={() => {
					const currentOption = normalizedOptions.find((opt) => opt.value === value);
					if (!currentOption && value) {
						// сохраняем введённый текст даже если он не из списка
						if (setValues) {
							setValues({ ...values, [name]: value });
						}
					}
				}}
				noOptionsText={<NoOptionText />}
				disableClearable={!value}
				clearIcon={<FontAwesomeIcon icon={faXmark} size="xs" />}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						name={name}
						value={value}
						inputRef={inputRef}
						// onChange={handleChangeInput}
						InputLabelProps={{ shrink: shrink, style: { fontSize: 12 } }}
						sx={{
							".MuiInputBase-root": { height: 33, fontSize: 13, color: colorPrimary },
							".MuiInputBase-input": { marginTop: -1 },
							".MuiInputLabel-root[data-shrink='false']": { top: -8 },
							".MuiOutlinedInput-root": { padding: "0 0 0 9px", paddingRight: "30px !important" },
						}}
					/>
				)}
			/>
		);
	}

	// Если нет опций, просто TextField
	return (
		<TextField
			label={label}
			value={value}
			name={name}
			onChange={handleChangeInput}
			variant="outlined"
			inputRef={inputRef}
			InputProps={{ style: { height: 33, fontSize: 13, color: colorPrimary } }}
			InputLabelProps={{ shrink: shrink, style: { fontSize: 13 } }}
			sx={{ ".MuiInputLabel-root[data-shrink='false']": { top: -8 }, width: "100%" }}
		/>
	);
};

export default TextfieldWithAutocomplete;
